import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RouteUrls } from "../../../Constants/RouteUrls";
import { registrationFormData } from "./Data";
import { FormDataType } from "DataTypes/Form";
import { commonFormSwitch, ControlledBoxSizeSwitch } from "Utils/SwitchCases";
import { HandleFormChange } from "Utils/Common";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ControlledForm from "Components/ControlledForm";
import authApi from "Service/Auth.api";
import OtpInput from "Components/OtpInput";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../../Redux/Screen/Action";

const CustomerRegister = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlPath = window.location.pathname;
  const isCorporate = urlPath.includes(RouteUrls.corporate);
  const [formData, setFormData] = useState<FormDataType[]>([]);

  // Validation Helpers
  const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone: string): boolean => /^\d{10}$/.test(phone);

  const [emailData, setEmailData] = useState<{
    fieldVal: string;
    otpSend: boolean;
    otpVal: string;
    otpVerified: boolean;
  }>({
    fieldVal: "",
    otpSend: false,
    otpVal: "",
    otpVerified: false,
  });
  const [contactData, setContactData] = useState<{
    fieldVal: string;
    otpSend: boolean;
    otpVal: string;
    otpVerified: boolean;
  }>({
    fieldVal: "",
    otpSend: false,
    otpVal: "",
    otpVerified: false,
  });

  // Loading States
  const [emailLoading, setEmailLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  // Timers
  const [emailTimer, setEmailTimer] = useState(0);
  const emailTimerInterval = useRef<any>(null);

  const [contactTimer, setContactTimer] = useState(0);
  const contactTimerInterval = useRef<any>(null);

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!isCorporate) {
        setFormData(registrationFormData());
        return;
      }

      try {
        const res = await authApi.getOrganizations();
        const OrgDropArr = res?.data || [];
        const newArr = JSON.parse(JSON.stringify(registrationFormData()));
        newArr.push({
          id: "organization",
          label: "Organization",
          boxType: "autocomplete",
          dropArr: OrgDropArr,
          placeholder: "Type or Select Organization",
          value: "",
        });
        setFormData(newArr);
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
        setFormData(registrationFormData());
      }
    };
    fetchOrgs();
  }, [isCorporate]);

  // Timer Cleanup
  useEffect(() => {
    return () => {
      if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
      if (contactTimerInterval.current) clearInterval(contactTimerInterval.current);
    }
  }, []);

  const startEmailTimer = () => {
    if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
    setEmailTimer(30);
    emailTimerInterval.current = setInterval(() => {
      setEmailTimer((prev) => {
        if (prev <= 1) {
          clearInterval(emailTimerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const startContactTimer = () => {
    if (contactTimerInterval.current) clearInterval(contactTimerInterval.current);
    setContactTimer(30);
    contactTimerInterval.current = setInterval(() => {
      setContactTimer((prev) => {
        if (prev <= 1) {
          clearInterval(contactTimerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const handleSendOtp = async (otpType: "email" | "contact") => {
    if (otpType === "email") {
      // 1. Validation for Email
      if (!emailData.fieldVal || emailData.fieldVal.trim() === "") {
        dispatch(setAlertData({ alertMessage: "Please enter a valid email address first.", alertVariant: "error", openAlert: true }));
        return;
      }
      if (!validateEmail(emailData.fieldVal)) {
        dispatch(setAlertData({ alertMessage: "Please enter a valid email format.", alertVariant: "error", openAlert: true }));
        return;
      }
      setEmailLoading(true);
      try {
        const res = await authApi.emailSendOtp({ email: emailData.fieldVal, requestType: "register" });
        if (res?.success) {
          setEmailData(prev => ({ ...prev, otpSend: true, otpVal: "", otpVerified: false }));
          startEmailTimer();
        }
      } finally {
        setEmailLoading(false);
      }
    } else {
      // 2. Validation for Contact
      if (!contactData.fieldVal || contactData.fieldVal.trim() === "") {
        dispatch(setAlertData({ alertMessage: "Please enter your contact number first.", alertVariant: "error", openAlert: true }));
        return;
      }
      if (!validatePhone(contactData.fieldVal)) {
        dispatch(setAlertData({ alertMessage: "Please enter a valid 10-digit contact number.", alertVariant: "error", openAlert: true }));
        return;
      }
      setContactLoading(true);
      try {
        const res = await authApi.mobileSendOtp({ mobileNumber: contactData.fieldVal, requestType: "register" });
        if (res?.success) {
          setContactData(prev => ({ ...prev, otpSend: true, otpVal: "", otpVerified: false }));
          startContactTimer();
        }
      } finally {
        setContactLoading(false);
      }
    }
  };

  const handleVerifyOtp = async (otpType: "email" | "contact") => {
    if (otpType === "email") {
      if (emailData.otpVal.length !== 4) {
        dispatch(setAlertData({ alertMessage: "Please enter valid 4-digit OTP", alertVariant: "error", openAlert: true }));
        return;
      }
      setEmailLoading(true);
      try {
        const res = await authApi.verifyOtp({ mobileNumber: null, email: emailData.fieldVal, otp: emailData.otpVal });
        if (res?.success) {
          setEmailData(prev => ({ ...prev, otpVerified: true }));
          if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
          setEmailTimer(0);
        }
      } finally {
        setEmailLoading(false);
      }
    } else {
      if (contactData.otpVal.length !== 4) {
        dispatch(setAlertData({ alertMessage: "Please enter valid 4-digit OTP", alertVariant: "error", openAlert: true }));
        return;
      }
      setContactLoading(true);
      try {
        const res = await authApi.verifyOtp({ mobileNumber: contactData.fieldVal, email: null, otp: contactData.otpVal });
        if (res?.success) {
          setContactData(prev => ({ ...prev, otpVerified: true }));
          if (contactTimerInterval.current) clearInterval(contactTimerInterval.current);
          setContactTimer(0);
        }
      } finally {
        setContactLoading(false);
      }
    }
  };

  const handleRegister = async () => {
    const defaultDOB = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString();

    const payload = {
      firstName: formData[0]?.value || "User",
      lastName: formData[1]?.value || "New",
      email: emailData?.fieldVal || "",
      contactNumber: contactData?.fieldVal || "0000000000",
      password: formData[2]?.value || "",
      address: formData[8]?.value || "Not Provided",
      dateOfBirth: formData[6]?.value || defaultDOB,
      city: formData[3]?.value || "",
      state: formData[4]?.value || "",
      postalCode: formData[5]?.value || "",
      description: formData[7]?.value || "",
      highestQualification: "",
      profilePicturePath: ""
    };

    dispatch(startLoading({ loading: true, loadtext: "Registering..." }));
    try {
      const res = await authApi.registerUser(payload);
      if (res?.success) {
        navigate(`/${RouteUrls.login}`);
      }
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleCorporateRegister = async () => {
    const defaultDOB = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString();

    const payload = {
      firstName: formData[0]?.value ?? "",
      lastName: formData[1]?.value ?? "",
      password: formData[2]?.value ?? "",
      corporateOrg: formData.find(x => x.id === "organization")?.value?.toString() ?? "",
      address: formData[7]?.value ?? "Not Provided",
      city: formData[8]?.value ?? "",
      state: formData[4]?.dropValue ?? "",
      postalCode: formData[5]?.value ?? "",
      dateOfBirth: formData[6]?.value ?? defaultDOB,
      highestQualification: "",
      profilePicturePath: "",
      email: emailData?.fieldVal ?? "",
      contactNumber: contactData?.fieldVal ?? "",
    };

    dispatch(startLoading({ loading: true, loadtext: "Registering..." }));
    try {
      const res = await authApi.registerCorporateUser(payload);
      if (res?.success) {
        navigate(`/${RouteUrls.login}`);
      }
    } finally {
      dispatch(stopLoading());
    }
  };

  const handlePasswordToggle = (fieldId: string) => {
    setFormData((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, showPassword: !field.showPassword }
          : field
      )
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        overflowY: "auto"
      }}
    >
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src={"https://nolegalpaisa.com/assets/images/new_logo.webp"}
          alt="Logo"
          sx={{ height: "60px", cursor: "pointer" }}
          onClick={() => (window.location.href = "/home.html")}
        />
      </Box>

      <Container maxWidth="sm" sx={{ pb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 6,
            boxShadow: "0px 20px 50px rgba(0,0,0,0.08)",
            bgcolor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)"
          }}
        >
          <Box component="header" sx={{ textAlign: "center", mb: 4 }}>
            <Avatar sx={{ m: "0 auto 16px", bgcolor: "primary.main", width: 60, height: 60, boxShadow: "0 8px 20px rgba(25, 118, 210, 0.2)" }}>
              <LockOutlinedIcon fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h4" fontWeight={800} sx={{ color: "#1a202c", mb: 1 }}>
              {isCorporate ? "Corporate Sign Up" : "User Sign Up"}
            </Typography>
            <Typography variant="body1" color="text.primary">
              Create your account to get started
            </Typography>
          </Box>

          <Box component="form" noValidate>
            <Grid size={{ xs: 12, md: 12 }} spacing={2}>
              {formData.length > 0 &&
                formData.map((el: FormDataType) => (
                  <Grid
                    key={el.id}
                    {...ControlledBoxSizeSwitch(el.boxSize)}
                  >
                    {commonFormSwitch({
                      formObj: {
                        ...el,
                        onPasswordToggle: el.hasPasswordToggle
                          ? () => handlePasswordToggle(el.id)
                          : undefined,
                      },
                      onChange: (data) =>
                        setFormData(HandleFormChange(data, formData)),
                    })}
                  </Grid>
                ))}

              {/* Email Section */}
              {/* Email Section */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <ControlledForm.Input
                      id="email"
                      label="Email"
                      value={emailData.fieldVal}
                      {...(emailData.otpVerified && { focused: true, borderColor: "success", readOnly: true })}
                      onChange={(value) => setEmailData({ ...emailData, fieldVal: value as string })}
                    />
                  </Box>
                  {!emailData.otpVerified && (
                    <Box sx={{ width: '100%' }}>
                      {emailData.otpSend ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <OtpInput
                              length={4}
                              value={emailData.otpVal}
                              onChange={(val) => setEmailData(prev => ({ ...prev, otpVal: val }))}
                              onComplete={() => { }}
                            />
                          </Box>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => handleVerifyOtp("email")}
                            disabled={emailLoading}
                            sx={{ textTransform: 'none', height: '40px' }}
                          >
                            Verify
                          </Button>
                          <Button
                            fullWidth
                            disabled={emailTimer > 0 || emailLoading || !validateEmail(emailData.fieldVal)}
                            onClick={() => handleSendOtp("email")}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            size="small"
                          >
                            {emailTimer > 0 ? `${emailTimer}s` : "Resend"}
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{ height: '56px', textTransform: 'none' }}
                          onClick={() => handleSendOtp("email")}
                          disabled={emailLoading || !validateEmail(emailData.fieldVal)}
                        >
                          Send OTP
                        </Button>
                      )}
                    </Box>
                  )}
                  {emailData.otpVerified && (
                    <Box sx={{ width: '100%' }}>
                      <Typography color="success.main" variant="body2" fontWeight="bold">Verified</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Contact Section */}
              {/* Contact Section */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <ControlledForm.Input
                      id="contact"
                      label="Contact"
                      value={contactData.fieldVal}
                      {...(contactData.otpVerified && { focused: true, borderColor: "success", readOnly: true })}
                      onChange={(value) => {
                        const val = value as string;
                        if (/^\d*$/.test(val)) { // Only allow digits
                          setContactData({ ...contactData, fieldVal: val.slice(0, 10) })
                        }
                      }}
                    />
                  </Box>
                  {!contactData.otpVerified && (
                    <Box sx={{ width: '100%' }}>
                      {contactData.otpSend ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <OtpInput
                              length={4}
                              value={contactData.otpVal}
                              onChange={(val) => setContactData(prev => ({ ...prev, otpVal: val }))}
                              onComplete={() => { }}
                            />
                          </Box>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => handleVerifyOtp("contact")}
                            disabled={contactLoading}
                            sx={{ textTransform: 'none', height: '40px' }}
                          >
                            Verify
                          </Button>
                          <Button
                            fullWidth
                            disabled={contactTimer > 0 || contactLoading || !validatePhone(contactData.fieldVal)}
                            onClick={() => handleSendOtp("contact")}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                            size="small"
                          >
                            {contactTimer > 0 ? `${contactTimer}s` : "Resend"}
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{ height: '56px', textTransform: 'none' }}
                          onClick={() => handleSendOtp("contact")}
                          disabled={contactLoading || !validatePhone(contactData.fieldVal)}
                        >
                          Send OTP
                        </Button>
                      )}
                    </Box>
                  )}
                  {contactData.otpVerified && (
                    <Box sx={{ width: '100%' }}>
                      <Typography color="success.main" variant="body2" fontWeight="bold">Verified</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={isCorporate ? handleCorporateRegister : handleRegister}
                  disabled={!emailData.otpVerified || !contactData.otpVerified}
                  sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: "bold", borderRadius: 3 }}
                >
                  Sign Up
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 12 }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    textDecoration: "underline",
                    cursor: "pointer",
                    mt: 2,
                    color: "primary.main",
                    fontWeight: 700
                  }}
                  variant="body2"
                  onClick={() => navigate(`/${RouteUrls.login}`)}
                >
                  Already have an account? Login
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
export default CustomerRegister;
